import React, { useEffect, useState } from "react";
import { Plus, Trash, ForkKnife } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];

export default function Nutrition() {
  const [meals, setMeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", meal_type: "Breakfast", calories: 0, protein: 0, carbs: 0, fat: 0 });

  const load = () => api.get("/meals").then(({ data }) => setMeals(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Meal name required");
    try {
      await api.post("/meals", {
        ...form,
        calories: Number(form.calories) || 0,
        protein: Number(form.protein) || 0,
        carbs: Number(form.carbs) || 0,
        fat: Number(form.fat) || 0,
      });
      toast.success("Meal logged.");
      setOpen(false);
      setForm({ name: "", meal_type: "Breakfast", calories: 0, protein: 0, carbs: 0, fat: 0 });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/meals/${id}`);
    toast.success("Deleted");
    load();
  };

  // Today's totals
  const today = new Date().toISOString().slice(0, 10);
  const todayMeals = meals.filter((m) => m.date?.startsWith(today));
  const totals = todayMeals.reduce(
    (acc, m) => ({
      calories: acc.calories + (m.calories || 0),
      protein: acc.protein + (m.protein || 0),
      carbs: acc.carbs + (m.carbs || 0),
      fat: acc.fat + (m.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="nutrition-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// FUEL LOG</div>
          <h1 className="font-heading text-4xl sm:text-5xl">NUTRITION</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="add-meal-button">
              <Plus size={14} weight="bold" /> LOG MEAL
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-lg" data-testid="meal-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">LOG MEAL</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="label-tiny block mb-2">NAME</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chicken bowl" className="bg-surface-elevated border-white/10" data-testid="meal-name-input" />
              </div>
              <div>
                <label className="label-tiny block mb-2">MEAL TYPE</label>
                <Select value={form.meal_type} onValueChange={(v) => setForm({ ...form, meal_type: v })}>
                  <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="meal-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                    {MEAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {["calories", "protein", "carbs", "fat"].map((k) => (
                  <div key={k}>
                    <label className="label-tiny block mb-2">{k.toUpperCase()}</label>
                    <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-surface-elevated border-white/10" data-testid={`meal-${k}-input`} />
                  </div>
                ))}
              </div>
              <button onClick={save} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest" data-testid="save-meal-button">
                SAVE MEAL
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Today's totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "CALORIES TODAY", val: totals.calories, unit: "kcal", testid: "total-calories" },
          { label: "PROTEIN", val: totals.protein.toFixed(0), unit: "g", testid: "total-protein" },
          { label: "CARBS", val: totals.carbs.toFixed(0), unit: "g", testid: "total-carbs" },
          { label: "FAT", val: totals.fat.toFixed(0), unit: "g", testid: "total-fat" },
        ].map((s) => (
          <div key={s.label} className="tactical-card p-5" data-testid={s.testid}>
            <div className="label-tiny mb-2">{s.label}</div>
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-3xl tabular-nums">{s.val}</span>
              <span className="label-tiny">{s.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {meals.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="meals-empty">
          <ForkKnife size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">NO MEALS LOGGED</h3>
          <p className="text-sm text-text-secondary">Track your first meal to start the data flow.</p>
        </div>
      ) : (
        <div className="tactical-card overflow-hidden" data-testid="meals-list">
          <table className="w-full text-sm">
            <thead className="bg-surface-elevated">
              <tr className="text-left label-tiny">
                <th className="px-5 py-3">MEAL</th>
                <th className="px-5 py-3">TYPE</th>
                <th className="px-5 py-3 text-right">KCAL</th>
                <th className="px-5 py-3 text-right">P/C/F</th>
                <th className="px-5 py-3">DATE</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {meals.map((m) => (
                <tr key={m.meal_id} className="border-t border-white/5 hover:bg-surface-elevated/50" data-testid={`meal-row-${m.meal_id}`}>
                  <td className="px-5 py-3">{m.name}</td>
                  <td className="px-5 py-3 text-brand-accent label-tiny">{m.meal_type}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{m.calories}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-text-secondary">{m.protein}/{m.carbs}/{m.fat}</td>
                  <td className="px-5 py-3 text-text-secondary">{new Date(m.date).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(m.meal_id)} className="text-text-secondary hover:text-red-400" data-testid={`delete-meal-${m.meal_id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
